import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text, Image } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Posts, PostStatus } from '@/model';
import { useTranslation } from 'react-i18next';

export interface PostsTableProps {
  data: Posts[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick?: (item: Posts) => void;
  onDeleteClick?: (item: Posts) => void;
}

export const PostsTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: PostsTableProps) => {
  const { t } = useTranslation();

  const columnHelper = createColumnHelper<Posts>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_posts.components.posts_table.posts_table_header_created_at'),
    }),
    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name || ''} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name || ''}</Text>
          </Flex>
        );
      },
      header: t('pages.application_posts.components.posts_table.posts_table_header_team'),
    }),
    columnHelper.accessor('updated_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue() ?? ''),
      header: t('pages.application_posts.components.posts_table.posts_table_header_updated_at'),
    }),
    columnHelper.accessor('content', {
      cell: props => props.getValue(),
      header: t('pages.application_posts.components.posts_table.posts_table_header_content'),
    }),
    columnHelper.accessor('identifier', {
      cell: props => props.getValue(),
      header: t('pages.application_posts.components.posts_table.posts_table_header_identifier'),
    }),    
    columnHelper.accessor('total_likes', {
      cell: props => props.getValue(),
      header: t('pages.application_posts.components.posts_table.posts_table_header_total_likes'),
    }),
    columnHelper.accessor('total_comments', {
      cell: props => props.getValue(),
      header: t('pages.application_posts.components.posts_table.posts_table_header_total_comments'),
    }),
    columnHelper.accessor('status', {
      cell: props => (
        <Text>
          {props.getValue() === PostStatus.PUBLISHED &&
            t('pages.application_posts.components.posts_table.posts_table_status_published')}
          {props.getValue() === PostStatus.NOT_PUBLISHED &&
            t('pages.application_posts.components.posts_table.posts_table_status_not_published')}
          {props.getValue() === PostStatus.DRAFT && t('pages.application_posts.components.posts_table.posts_table_status_draft')}
          {props.getValue() === PostStatus.DELETED &&
            t('pages.application_posts.components.posts_table.posts_table_status_deleted')}
        </Text>
      ),
      header: t('pages.application_posts.components.posts_table.posts_table_header_status'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.application_posts.components.posts_table.posts_table_header_actions'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_posts.components.posts_table.posts_table_actions_aria_label')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as Posts)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='edit-2' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_posts.components.posts_table.posts_table_action_edit')}</Text>
              </MenuItem>

              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as Posts)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text color='red.500'>{t('pages.application_posts.components.posts_table.posts_table_action_delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
